import type { WhatsAppMessage } from './types';

export type MessagesByChatId = { [chatId: string]: WhatsAppMessage[] };

export interface SendMessageSuccessData {
    clientMessageId: string;
    messageId?: string;
    ack?: number;
    timestamp?: number;
}

export const markPendingMessagesUnknown = (
    prevMessages: MessagesByChatId,
    errorMessage: string
): MessagesByChatId => {
    let hasChanges = false;
    const nextMessages: MessagesByChatId = {};

    for (const [chatId, chatMessages] of Object.entries(prevMessages)) {
        let chatHasChanges = false;
        const nextChatMessages = chatMessages.map(message => {
            const isPendingOutgoing =
                message.direction === 'outgoing' &&
                message.sendStatus === 'pending';

            if (!isPendingOutgoing) return message;

            hasChanges = true;
            chatHasChanges = true;
            return {
                ...message,
                sendStatus: 'unknown' as const,
                sendError: errorMessage
            };
        });

        nextMessages[chatId] = chatHasChanges ? nextChatMessages : chatMessages;
    }

    return hasChanges ? nextMessages : prevMessages;
};

export const getSendStatusFromAck = (ack?: number): WhatsAppMessage['sendStatus'] => {
    if (typeof ack === 'number' && ack < 0) return 'failed';
    if (typeof ack === 'number' && ack >= 1) return 'sent';
    return 'pending';
};

const getSendErrorFromAck = (ack?: number): string | undefined => (
    typeof ack === 'number' && ack < 0
        ? 'WhatsApp отклонил отправку сообщения.'
        : undefined
);

const getSendSuccessAck = (
    data: SendMessageSuccessData,
    currentAck?: number,
    bufferedAck?: number
): number | undefined => {
    const knownAcks = [bufferedAck, data.ack, currentAck]
        .filter((ack): ack is number => typeof ack === 'number' && Number.isFinite(ack));
    const rejectedAck = knownAcks.find(ack => ack < 0);

    if (rejectedAck !== undefined) return rejectedAck;

    const highestAck = knownAcks.length ? Math.max(...knownAcks) : undefined;

    // A successful response with a stable WhatsApp id confirms creation even
    // when the Message object still contains ACK 0. Do not leave the UI stuck
    // in "Отправка..." if the separate ACK event was missed or arrived early.
    if (data.messageId) return Math.max(1, highestAck ?? 0);

    return highestAck;
};

export const applyMessageAck = (
    prevMessages: MessagesByChatId,
    messageId: string,
    ack: number
): { messages: MessagesByChatId; matched: boolean } => {
    let matched = false;
    const nextMessages = { ...prevMessages };

    for (const chatId in nextMessages) {
        const messageIndex = nextMessages[chatId].findIndex(message => message.id === messageId);
        if (messageIndex === -1) continue;

        const updatedMessages = [...nextMessages[chatId]];
        updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            ack,
            sendStatus: getSendStatusFromAck(ack),
            sendError: getSendErrorFromAck(ack)
        };
        nextMessages[chatId] = updatedMessages;
        matched = true;
    }

    return {
        messages: matched ? nextMessages : prevMessages,
        matched
    };
};

export const mergeMessageSendState = (
    currentMessage: WhatsAppMessage,
    incomingMessage: WhatsAppMessage
): WhatsAppMessage => {
    const mergedMessage = { ...currentMessage, ...incomingMessage };
    const currentAck = typeof currentMessage.ack === 'number' && Number.isFinite(currentMessage.ack)
        ? currentMessage.ack
        : undefined;
    const incomingAck = typeof incomingMessage.ack === 'number' && Number.isFinite(incomingMessage.ack)
        ? incomingMessage.ack
        : undefined;

    if (incomingAck !== undefined && incomingAck < 0) {
        return {
            ...mergedMessage,
            ack: incomingAck,
            sendStatus: 'failed',
            sendError: getSendErrorFromAck(incomingAck)
        };
    }

    if (currentAck !== undefined && currentAck < 0) {
        return {
            ...mergedMessage,
            ack: currentAck,
            sendStatus: 'failed',
            sendError: currentMessage.sendError || getSendErrorFromAck(currentAck)
        };
    }

    const confirmedAck = Math.max(currentAck ?? 0, incomingAck ?? 0);
    if (confirmedAck >= 1) {
        return {
            ...mergedMessage,
            ack: confirmedAck,
            sendStatus: 'sent',
            sendError: undefined
        };
    }

    if (currentMessage.sendStatus === 'unknown' && incomingMessage.sendStatus === 'pending') {
        return {
            ...mergedMessage,
            ack: currentAck ?? incomingAck,
            sendStatus: 'unknown',
            sendError: currentMessage.sendError
        };
    }

    return mergedMessage;
};

export const reconcileSendSuccess = (
    prevMessages: MessagesByChatId,
    data: SendMessageSuccessData,
    bufferedAck?: number
): { messages: MessagesByChatId; matched: boolean } => {
    let matched = false;
    const nextMessages = { ...prevMessages };

    for (const chatId in nextMessages) {
        const messageIndex = nextMessages[chatId].findIndex(message => (
            message.id === data.clientMessageId ||
            Boolean(data.messageId && message.id === data.messageId)
        ));
        if (messageIndex === -1) continue;

        const updatedMessages = [...nextMessages[chatId]];
        const currentMessage = updatedMessages[messageIndex];
        const ack = getSendSuccessAck(data, currentMessage.ack, bufferedAck);

        updatedMessages[messageIndex] = {
            ...currentMessage,
            id: data.messageId || currentMessage.id,
            timestamp: data.timestamp ? data.timestamp * 1000 : currentMessage.timestamp,
            ack,
            sendStatus: getSendStatusFromAck(ack),
            sendError: getSendErrorFromAck(ack)
        };
        nextMessages[chatId] = updatedMessages;
        matched = true;
    }

    return {
        messages: matched ? nextMessages : prevMessages,
        matched
    };
};
